import { api } from './client';
import { PAGINATION } from '@/lib/constants';
import type {
  AccountActivePatchRequest,
  AccountCreateRequest,
  AccountIdentityPatchRequest,
  AccountResponse,
  BulkImportResponse,
  CheckNowResponse,
  Page,
  PageParams,
} from '@/types/api';

export type AccountsListParams = PageParams;

export const accountsApi = {
  async list(params: AccountsListParams = {}): Promise<Page<AccountResponse>> {
    const res = await api.get<Page<AccountResponse>>('/accounts', {
      params: {
        page: params.page ?? 0,
        size: params.size ?? PAGINATION.defaultSize,
        sort: params.sort ?? 'username,asc',
      },
    });
    return res.data;
  },

  async get(id: string): Promise<AccountResponse> {
    const res = await api.get<AccountResponse>(`/accounts/${id}`);
    return res.data;
  },

  async create(body: AccountCreateRequest): Promise<AccountResponse> {
    const res = await api.post<AccountResponse>('/accounts', body);
    return res.data;
  },

  async bulkImport(file: File): Promise<BulkImportResponse> {
    const form = new FormData();
    form.append('file', file);
    // Axios détecte FormData et génère le bon `multipart/form-data; boundary=...`
    // SEULEMENT si on ne force pas Content-Type en amont. On passe la config via
    // `transformRequest` pour supprimer l'en-tête JSON par défaut de l'instance.
    const res = await api.post<BulkImportResponse>('/accounts/bulk', form, {
      transformRequest: [
        (data, headers) => {
          if (headers) delete headers['Content-Type'];
          return data;
        },
      ],
    });
    return res.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/accounts/${id}`);
  },

  async patchIdentity(id: string, body: AccountIdentityPatchRequest): Promise<AccountResponse> {
    const res = await api.patch<AccountResponse>(`/accounts/${id}/identity`, body);
    return res.data;
  },

  async patchActive(id: string, body: AccountActivePatchRequest): Promise<AccountResponse> {
    const res = await api.patch<AccountResponse>(`/accounts/${id}/active`, body);
    return res.data;
  },

  /**
   * Returns the response PLUS the `X-RateLimit-Limit` and `X-RateLimit-Remaining`
   * headers so the UI can surface the remaining daily quota dynamically.
   */
  async checkNow(
    id: string,
  ): Promise<{ data: CheckNowResponse; remaining: number | null; limit: number | null }> {
    const res = await api.post<CheckNowResponse>(`/accounts/${id}/check-now`);
    return {
      data: res.data,
      remaining: parseIntHeader(res.headers['x-ratelimit-remaining']),
      limit: parseIntHeader(res.headers['x-ratelimit-limit']),
    };
  },
};

function parseIntHeader(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}
